// Multiplayer lobby contracts. Pre-game state: a Match row with
// status='pending' is a "lobby" — players can join via lobby_code,
// toggle ready, and the host can start the match (status flips to
// 'active' and the orchestrator takes over).
import { z } from 'zod';
import { PlayerIconSchema } from './solo.js';
// Per-match "hero" pick. The user is choosing a fresh display name +
// team name + icon for THIS multi match. Distinct from any solo
// identity. The server uses these to create a throwaway Player row
// for the match and sets the multi_player_id cookie to that row.
const TrimmedString = (min, max) => z
    .string()
    .transform((s) => s.trim())
    .pipe(z.string().min(min).max(max));
export const HeroIdentitySchema = z.object({
    display_name: TrimmedString(1, 24),
    team_name: TrimmedString(1, 24),
    icon: PlayerIconSchema,
});
// Host-selectable shop-phase duration. NULL = no timer; round resolves
// only when every alive participant clicks ready. With a numeric value,
// the timer fires after that many seconds since round_start, at which
// point unready alive participants are auto-readied (so the chip state
// reflects the resolution) and the round resolves.
//
// Disconnect handling (NEW): when a player's socket dies, a 60s grace
// timer ticks down on their roster chip. If they don't reconnect, they
// get auto-readied. Host can also /kick (instant auto-ready) any
// non-ready alive participant to unblock a stalled no-timer round.
export const RoundTimerOptions = [null, 60, 120, 180, 240];
export const RoundTimerOptionsSchema = z.union([
    z.null(),
    z.literal(60),
    z.literal(120),
    z.literal(180),
    z.literal(240),
]);
/** Default — no host timer. Round advances on universal ready (+ host
 *  kick / 60s disconnect grace as escape hatches). */
export const DEFAULT_ROUND_TIMER_SECONDS = null;
/** 60s default for the per-player disconnect grace window. Exposed
 *  here so the client roster chip can format the countdown and tests
 *  can drive a shorter value when needed. */
export const DEFAULT_DISCONNECT_GRACE_MS = 60_000;
export const MAX_LOBBY_PLAYERS = 8;
export const MIN_LOBBY_PLAYERS_TO_START = 2;
// Lobby-code alphabet excludes visually-confusable characters (0/O, 1/I/L).
export const LOBBY_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
export const LOBBY_CODE_LENGTH = 6;
export const LobbyCodeSchema = z
    .string()
    .length(LOBBY_CODE_LENGTH)
    .regex(new RegExp(`^[${LOBBY_CODE_ALPHABET}]+$`), 'invalid lobby code');
export const CreateLobbySchema = z.object({
    round_timer_seconds: RoundTimerOptionsSchema.optional(),
    /** The hero the player just picked for this match (display name +
     *  team name + icon). Required — every multi entry goes through
     *  the inscribe step. Server creates a fresh Player row from
     *  these values and sets the multi cookie. */
    identity: HeroIdentitySchema,
});
export const JoinLobbySchema = z.object({
    lobby_code: LobbyCodeSchema,
    identity: HeroIdentitySchema,
});
// Lobby-specific error codes layered on top of the shop SoloErrorCodes.
// Listed here so the server's error envelope is typed-end-to-end.
export const LobbyErrorCodes = [
    'lobby_not_found',
    'lobby_full',
    'lobby_already_started',
    'already_in_lobby',
    'not_host',
    'not_in_lobby',
    'match_not_active',
    'not_in_active_round',
    'invalid_lobby_code',
    'too_few_players',
    /** Host tried to /start but at least one alive participant
     *  still has ready=false. Client should surface a hint and wait
     *  for the lobby_update push to refresh the roster. */
    'not_all_ready',
    /** /api/multi/kick — host tried to kick themselves. */
    'cant_kick_self',
    /** /api/multi/kick — target is already ready, nothing to do. */
    'target_already_ready',
    /** /api/multi/kick — target is eliminated; they're already out
     *  of the ready check. */
    'target_eliminated',
    /** /api/multi/kick — body referenced a participant_id that isn't
     *  in this match. */
    'target_not_found',
];
export const LobbyErrorCodeSchema = z.enum(LobbyErrorCodes);
// Host-only request to mark a stalled / disconnected participant as
// ready so the round can resolve. Reuses the participant_id from the
// LobbyStatePublic roster.
export const KickPlayerSchema = z.object({
    participant_id: z.string().uuid(),
});
export { PlayerIconSchema };
//# sourceMappingURL=lobby.js.map